/**
 * Horizontal Stacked Bar Chart Type Definition
 */

import {
  paletteControl,
  numCategoriesControl,
  numStacksControl,
  gridControl,
  showValuesControl,
  valueDecimalsControl,
  axisControlsHorizontal,
  referenceLineControls,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  xAxisRangeCode,
  referenceLineCode,
  spineCode,
  finishCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornGridCode,
  seabornAxisLabelsCode,
  seabornXAxisRangeCode,
  seabornReferenceLineCode,
  seabornSpineCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';

import { getColors } from '../shared/palettes';
import { SERIES_HATCHES_MPL } from '../../utils/accessibility';

const showLegendControl = {
  key: 'showLegend',
  label: 'Show Legend',
  type: 'toggle',
};

const horizontalStackedBar = {
  id: 'horizontal-stacked-bar',
  name: 'Horizontal Stacked Bar Chart',
  category: 'bar',
  description: 'Show composition of totals with horizontal bars',

  sampleData: [
    { name: 'Cat 1', stack1: 45, stack2: 40, stack3: 30 },
    { name: 'Cat 2', stack1: 55, stack2: 35, stack3: 25 },
    { name: 'Cat 3', stack1: 35, stack2: 45, stack3: 30 },
    { name: 'Cat 4', stack1: 50, stack2: 35, stack3: 30 },
  ],

  defaultConfig: {
    externalColorMode: 'palette',
    useBluesPalette: true,
    useRedsPalette: false,
    useGreensPalette: false,
    numCategories: 4,
    numStacks: 3,
    outputFormat: 'matplotlib',
    showGrid: true,
    showValues: false,
    valueDecimals: 0,
    ylabel: 'Category',
    xlabel: 'Value',
    title: '',
    xMin: '',
    xMax: '',
    showReferenceLine: false,
    referenceValue: '',
    referenceLabel: 'Target',
    usePatternFills: false,
    showLegend: true,
    colorOverrides: undefined,
  },

  controls: [
    paletteControl,
    numCategoriesControl,
    numStacksControl,
    gridControl,
    showValuesControl,
    valueDecimalsControl,
    {
      key: 'usePatternFills',
      label: 'Use Pattern Fills',
      type: 'toggle',
    },
    showLegendControl,
    ...axisControlsHorizontal,
    ...referenceLineControls,
  ],

  generateCode: (config, dataContext, s) => {
    const hasWideUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.inputShape === 'wide' && Array.isArray(dataContext.mapping?.valueColumns) && dataContext.mapping.valueColumns.length > 0;
    const hasLongUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.value && dataContext.mapping?.series;
    const hasUserData = hasWideUserData || hasLongUserData;
    const numCategories = config.numCategories || 4;
    const numStacks = config.numStacks || 3;
    const colors = getColors(config, hasWideUserData ? dataContext.mapping.valueColumns.length : numStacks, s).reverse();
    const hatches = JSON.stringify(SERIES_HATCHES_MPL);
    const usePatternFills = !!config.usePatternFills;
    const categories = Array.from({ length: numCategories }, (_, i) => `Cat ${i + 1}`);

    if (config.outputFormat === 'seaborn') {
      const stackLines = [];
      for (let j = 0; j < numStacks; j++) {
        const values = Array.from({ length: numCategories }, () => Math.floor(20 + Math.random() * 40));
        stackLines.push(`    'Stack ${j + 1}': [${values.join(', ')}]`);
      }

      return `${seabornSetup(s)}

# ======== DATA ========
${hasUserData ? (hasWideUserData
  ? `df = pd.read_csv('${dataContext.fileName}')
df = df.set_index('${dataContext.mapping.category}')[${JSON.stringify(dataContext.mapping.valueColumns)}]`
  : `df = pd.read_csv('${dataContext.fileName}')
df = df.pivot_table(index='${dataContext.mapping.category}', columns='${dataContext.mapping.series}', values='${dataContext.mapping.value}', aggfunc='first')`) : `df = pd.DataFrame({
    'category': ${JSON.stringify(categories)},
${stackLines.join(',\n')}
})
df = df.set_index('category')`}
# ======================
${seabornCreateFigure(5.5, 4.5, 300, s)}

colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

df.plot(kind='barh', stacked=True, color=colors, edgecolor='${s?.axisColor || '#000000'}',
        linewidth=1.0, ax=ax, width=0.7)
${config.usePatternFills ? `for i, container in enumerate(ax.containers):
    for bar in container:
        bar.set_hatch(hatch_patterns[i % len(hatch_patterns)])` : '# Pattern fills disabled'}
${seabornTitleCode(config, s)}
${seabornAxisLabelsCode(config, s)}
${seabornGridCode(config, 'x', s)}

${config.showValues ? `for container in ax.containers:
    for bar in container:
        width = bar.get_width()
        if width > 0:
            ax.text(bar.get_x() + width/2., bar.get_y() + bar.get_height()/2.,
                    f'{width:.${config.valueDecimals || 0}f}',
                    ha='center', va='center', fontsize=${s?.valueLabelSize || 12},
                    color=_coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}'))` : '# Value labels disabled'}

${seabornXAxisRangeCode(config)}
${config.showLegend ? `ax.legend(title='', loc='upper right', fontsize=${s?.legendSize || 10})` : `ax.legend_.remove()`}
${seabornReferenceLineCode(config, 'vertical', s)}
${seabornSpineCode(s, 'number', 'label')}
${seabornFinishCode('horizontal_stacked_bar_chart')}`;
    }

    const stackCode = Array.from({ length: numStacks }, (_, i) => {
      const values = Array.from({ length: numCategories }, () => Math.floor(20 + Math.random() * 40));
      return `stack${i + 1} = [${values.join(', ')}]`;
    }).join('\n');
    const barCodes = Array.from({ length: numStacks }, (_, i) => {
      if (i === 0) {
        return `bars${i + 1} = ax.barh(categories, stack${i + 1}, label='Stack ${i + 1}', color=colors[${i}], hatch=(hatch_patterns[${i} % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))`;
      }
      const leftParts = Array.from({ length: i }, (_, j) => `np.array(stack${j + 1})`).join(' + ');
      return `bars${i + 1} = ax.barh(categories, stack${i + 1}, left=${leftParts}, label='Stack ${i + 1}', color=colors[${i}], hatch=(hatch_patterns[${i} % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))`;
    }).join('\n');

    if (hasUserData) {
      return `${matplotlibSetup(s)}

# ======== DATA ========
df = pd.read_csv('${dataContext.fileName}')
${hasWideUserData
  ? `pivot = df.set_index('${dataContext.mapping.category}')[${JSON.stringify(dataContext.mapping.valueColumns)}]`
  : `pivot = df.pivot_table(index='${dataContext.mapping.category}', columns='${dataContext.mapping.series}', values='${dataContext.mapping.value}', aggfunc='first')`}
categories = pivot.index.tolist()
stack_names = pivot.columns.tolist()
# ======================
${createFigure(5.5, 4.5, 300, s)}

colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}
left = np.zeros(len(categories))
for idx, stack_name in enumerate(stack_names):
    vals = pivot[stack_name].values
    ax.barh(categories, vals, left=left, label=stack_name, color=colors[idx % len(colors)],
            hatch=(hatch_patterns[idx % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))
    left += vals

${config.showValues ? `for container in ax.containers:
    for bar in container:
        width = bar.get_width()
        if width > 0:
            ax.text(bar.get_x() + width/2., bar.get_y() + bar.get_height()/2.,
                    f'{width:.${config.valueDecimals || 0}f}',
                    ha='center', va='center', fontsize=${s?.valueLabelSize || 12},
                    color=_coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}'))` : '# Value labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${gridCode(config, 'x', s)}
${xAxisRangeCode(config)}
${config.showLegend ? `ax.legend(loc='upper right', fontsize=${s?.legendSize || 10})` : '# Legend disabled'}
${referenceLineCode(config, 'vertical', s)}
${spineCode(s, 'number', 'label')}
${finishCode('horizontal_stacked_bar_chart')}`;
    }

    return `${matplotlibSetup(s)}

# ======== DATA ========
categories = ${JSON.stringify(categories)}
${stackCode}
# ======================
${createFigure(5.5, 4.5, 300, s)}

colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

${barCodes}
${config.showValues ? `for container in [${Array.from({ length: numStacks }, (_, i) => `bars${i + 1}`).join(', ')}]:
    for bar in container:
        width = bar.get_width()
        if width > 0:
            ax.text(bar.get_x() + width/2., bar.get_y() + bar.get_height()/2.,
                    f'{width:.${config.valueDecimals || 0}f}',
                    ha='center', va='center', fontsize=${s?.valueLabelSize || 12},
                    color=_coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}'))` : '# Value labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${gridCode(config, 'x', s)}
${xAxisRangeCode(config)}
${config.showLegend ? `ax.legend(loc='upper right', fontsize=${s?.legendSize || 10})` : '# Legend disabled'}
${referenceLineCode(config, 'vertical', s)}
${spineCode(s, 'number', 'label')}
${finishCode('horizontal_stacked_bar_chart')}`;
  },
};

export default horizontalStackedBar;
